import React, { useMemo, useState } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import { StepHeader, LABEL_CLASS, INPUT_SM_CLASS, DROPDOWN_TRIGGER_SM_CLASS } from '../components/StepLayout'

const FORMAT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'currency_inr', label: 'Currency (₹ INR)' },
  { value: 'currency_usd', label: 'Currency ($ USD)' },
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'number', label: 'Number (2 Decimals)' },
  { value: 'integer', label: 'Integer' },
  { value: 'date', label: 'Date (YYYY-MM-DD)' },
  { value: 'duration', label: 'Duration (Hours)' },
]

/**
 * The query builder only knows how to compile a single base table plus
 * one-hop joins declared on that table (see custom_reports_query_builder.py).
 * So "select several modules at once" really means: find a module in the
 * selection whose declared joins reach every OTHER selected module directly,
 * and use it as the base table. When the current primary is still a valid
 * anchor for the new selection we keep it, so toggling one more module
 * doesn't needlessly flip the base table (and the fields tied to it)
 * underneath the user.
 */
function computeAnchor(selectedIds, dataSources, preferredAnchor) {
  if (selectedIds.length === 0) return { anchor: null, secondary: [], valid: true }
  if (selectedIds.length === 1) return { anchor: selectedIds[0], secondary: [], valid: true }

  const byId = Object.fromEntries(dataSources.map((ds) => [ds.id, ds]))
  const candidates = selectedIds.filter((candidateId) => {
    const ds = byId[candidateId]
    if (!ds) return false
    const reachable = new Set((ds.allowedJoins || []).map((j) => j.targetModule))
    return selectedIds.every((otherId) => otherId === candidateId || reachable.has(otherId))
  })

  if (candidates.length === 0) return { anchor: null, secondary: [], valid: false }

  const anchor = candidates.includes(preferredAnchor) ? preferredAnchor : candidates[0]
  return { anchor, secondary: selectedIds.filter((id) => id !== anchor), valid: true }
}

/** Which currently-unselected modules would make an otherwise-invalid combination work. */
function findBridgeSuggestions(selectedIds, dataSources) {
  return dataSources
    .filter((ds) => !selectedIds.includes(ds.id))
    .filter((ds) => computeAnchor([...selectedIds, ds.id], dataSources, null).valid)
    .map((ds) => ds.id)
}

function defaultFieldRowsFor(allFields, moduleIds) {
  return allFields
    .filter((f) => moduleIds.includes(f.module) && f.defaultVisible)
    .map((f) => ({
      key: f.key,
      label: f.label,
      dataType: f.dataType,
      visible: true,
      format: f.dataType === 'currency' ? 'currency_inr' : null,
      width: null,
      order: 0,
    }))
}

export default function Step2DataSourceFields({
  primaryModule,
  setPrimaryModule,
  secondaryDatasets = [],
  setSecondaryDatasets,
  dataSources = [],
  selectedFields = [],
  setSelectedFields,
  allFields = [],
}) {
  const [search, setSearch] = useState('')
  const [conflict, setConflict] = useState(null) // { attempted: string[], suggestions: string[] } | null

  const selectedModules = useMemo(() => {
    const set = new Set(secondaryDatasets)
    if (primaryModule) set.add(primaryModule)
    return Array.from(set)
  }, [primaryModule, secondaryDatasets])

  const byId = useMemo(() => Object.fromEntries(dataSources.map((ds) => [ds.id, ds])), [dataSources])

  function applySelection(nextModules) {
    const { anchor, secondary, valid } = computeAnchor(nextModules, dataSources, primaryModule)
    if (!valid) {
      setConflict({ attempted: nextModules, suggestions: findBridgeSuggestions(nextModules, dataSources) })
      return false
    }
    setConflict(null)
    setPrimaryModule(anchor)
    setSecondaryDatasets(secondary)
    // Drop any selected field that belonged to a module which is no longer
    // part of the combination — otherwise the saved config could reference a
    // table alias that never gets joined.
    setSelectedFields((prev) =>
      prev.filter((f) => {
        const catField = allFields.find((cf) => cf.key === f.key)
        return catField && nextModules.includes(catField.module)
      })
    )
    return true
  }

  function seedDefaultsFor(moduleIds) {
    const defaults = defaultFieldRowsFor(allFields, moduleIds)
    setSelectedFields((prev) => {
      const existingKeys = new Set(prev.map((f) => f.key))
      const toAdd = defaults.filter((f) => !existingKeys.has(f.key))
      if (toAdd.length === 0) return prev
      return [...prev, ...toAdd].map((f, idx) => ({ ...f, order: idx }))
    })
  }

  function toggleModule(moduleId) {
    const isSelected = selectedModules.includes(moduleId)
    const nextModules = isSelected ? selectedModules.filter((m) => m !== moduleId) : [...selectedModules, moduleId]

    if (nextModules.length === 0) {
      setPrimaryModule('')
      setSecondaryDatasets([])
      setSelectedFields([])
      setConflict(null)
      return
    }

    const applied = applySelection(nextModules)
    if (applied && !isSelected) {
      // Picking a module already gives you a useful starting set of columns,
      // instead of an empty grid you have to build from scratch.
      seedDefaultsFor([moduleId])
    }
  }

  function applyBridge(bridgeId) {
    if (!conflict) return
    const nextModules = [...conflict.attempted, bridgeId]
    const applied = applySelection(nextModules)
    if (applied) {
      seedDefaultsFor([bridgeId, ...conflict.attempted])
    }
  }

  // ── Fields panel — driven by the full module set, not just one primary ──
  const relevantCatalogFields = useMemo(
    () => allFields.filter((f) => selectedModules.includes(f.module)),
    [allFields, selectedModules]
  )

  const filteredCatalogFields = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = relevantCatalogFields
    if (q) {
      list = list.filter((f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q))
    }
    return [...list].sort((a, b) => {
      if (a.frequent && !b.frequent) return -1
      if (!a.frequent && b.frequent) return 1
      return 0
    })
  }, [relevantCatalogFields, search])

  const selectedKeySet = useMemo(() => new Set(selectedFields.map((f) => f.key)), [selectedFields])

  function toggleField(catalogField) {
    if (selectedKeySet.has(catalogField.key)) {
      setSelectedFields((prev) => prev.filter((f) => f.key !== catalogField.key))
    } else {
      setSelectedFields((prev) => [
        ...prev,
        {
          key: catalogField.key,
          label: catalogField.label,
          dataType: catalogField.dataType,
          visible: true,
          format: catalogField.dataType === 'currency' ? 'currency_inr' : null,
          width: null,
          order: prev.length,
        },
      ])
    }
  }

  function moveField(index, direction) {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= selectedFields.length) return
    const updated = [...selectedFields]
    const temp = updated[index]
    updated[index] = updated[nextIndex]
    updated[nextIndex] = temp
    setSelectedFields(updated.map((item, idx) => ({ ...item, order: idx })))
  }

  function updateFieldProp(key, prop, value) {
    setSelectedFields((prev) => prev.map((f) => (f.key === key ? { ...f, [prop]: value } : f)))
  }

  const anchorLabel = byId[primaryModule]?.title

  return (
    <div className="space-y-3 max-w-5xl mx-auto py-1">
      <StepHeader
        title="Data Source & Fields"
        subtitle="Pick every module this report should pull from, then choose and arrange its columns."
      />

      {/* ── Module multi-select ── */}
      <div className="space-y-1.5">
        <label className={LABEL_CLASS}>Data Sources — select as many as you need</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
          {dataSources.map((ds) => {
            const isSelected = selectedModules.includes(ds.id)
            return (
              <button
                key={ds.id}
                type="button"
                onClick={() => toggleModule(ds.id)}
                className={`flex flex-col p-3 rounded-[8px] border text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/30 shadow-xs'
                    : 'border-surface-container-high bg-surface-container-low hover:bg-surface-container'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`material-symbols-outlined text-xl ${isSelected ? 'text-primary' : 'text-on-surface-variant'}`}>
                    {ds.icon || 'dataset'}
                  </span>
                  {isSelected && <span className="material-symbols-outlined text-primary text-base">check_circle</span>}
                </div>
                <h4 className="text-xs font-bold text-on-surface truncate">{ds.title}</h4>
                <p className="text-[10px] text-on-surface-variant line-clamp-1 mt-0.5">{ds.description}</p>
              </button>
            )
          })}
        </div>

        {selectedModules.length > 1 && !conflict && (
          <p className="text-[11px] text-on-surface-variant">
            Combined through <span className="font-bold text-on-surface">{anchorLabel}</span> as the base table.
          </p>
        )}

        {conflict && (
          <div className="p-3 rounded-[8px] border border-amber-500/30 bg-amber-500/5 space-y-2">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-base">link_off</span>
              These modules aren&apos;t directly connected, so they can&apos;t share one report yet.
            </p>
            {conflict.suggestions.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-on-surface-variant">Add a connecting module:</span>
                {conflict.suggestions.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => applyBridge(id)}
                    className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-500/15 text-amber-800 hover:bg-amber-500/25"
                  >
                    + {byId[id]?.title || id}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-on-surface-variant">
                Try removing one of the modules you just selected, or build these as two separate reports.
              </p>
            )}
            <button
              type="button"
              onClick={() => setConflict(null)}
              className="text-[11px] font-bold text-on-surface-variant hover:text-on-surface underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* ── Fields panel ── */}
      {selectedModules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 border border-dashed border-surface-container-high rounded-[10px] text-center">
          <span className="material-symbols-outlined text-3xl text-slate-300 mb-1">dataset</span>
          <p className="text-xs font-semibold text-on-surface">Select at least one data source above</p>
          <p className="text-[10px] text-on-surface-variant">Its fields will appear here once picked.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          {/* Left Column: Field Picker */}
          <div className="md:col-span-5 flex flex-col space-y-2.5 bg-surface-container-low p-3.5 rounded-[10px] border border-surface-container">
            <div className="flex items-center justify-between">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-on-surface">
                Available Fields ({relevantCatalogFields.length})
              </h4>
              <span className="text-[9px] bg-surface-container px-1.5 py-0.5 rounded text-on-surface-variant font-semibold">
                Frequent First
              </span>
            </div>

            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 top-2.5 text-slate-400 text-sm">search</span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search fields..."
                className="h-10 w-full pl-9 pr-3 rounded-[8px] border border-surface-container-high bg-surface text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="flex-1 overflow-y-auto max-h-[380px] space-y-1 pr-1">
              {filteredCatalogFields.map((f) => {
                const isSelected = selectedKeySet.has(f.key)
                return (
                  <div
                    key={f.key}
                    onClick={() => toggleField(f)}
                    className={`flex items-center justify-between p-2 rounded-[6px] border cursor-pointer transition-colors text-xs ${
                      isSelected
                        ? 'border-primary bg-primary/10 font-bold text-primary'
                        : 'border-transparent bg-surface hover:bg-surface-container text-on-surface'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="h-3.5 w-3.5 text-primary rounded border-surface-container-high focus:ring-primary"
                      />
                      <span className="truncate text-xs">{f.label}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {selectedModules.length > 1 && (
                        <span className="text-[8px] uppercase text-on-surface-variant/70 bg-surface-container px-1 py-0.2 rounded">
                          {byId[f.module]?.title || f.module}
                        </span>
                      )}
                      {f.frequent && (
                        <span className="material-symbols-outlined text-amber-500 text-xs" title="Frequently Used">
                          star
                        </span>
                      )}
                      <span className="text-[9px] uppercase text-on-surface-variant bg-surface-container-high px-1.5 py-0.2 rounded font-semibold">
                        {f.dataType}
                      </span>
                    </div>
                  </div>
                )
              })}
              {filteredCatalogFields.length === 0 && (
                <p className="text-xs text-on-surface-variant text-center py-4">No matching fields found.</p>
              )}
            </div>
          </div>

          {/* Right Column: Selected Columns Config */}
          <div className="md:col-span-7 flex flex-col space-y-2.5 bg-surface-container-low p-3.5 rounded-[10px] border border-surface-container">
            <div className="flex items-center justify-between">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-on-surface">
                Selected Columns ({selectedFields.length})
              </h4>
              <span className="text-[11px] text-on-surface-variant">Reorder &amp; customize display</span>
            </div>

            {selectedFields.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-52 border border-dashed border-surface-container-high rounded-[8px] text-center p-4">
                <span className="material-symbols-outlined text-3xl text-slate-300 mb-1">view_column</span>
                <p className="text-xs font-semibold text-on-surface mb-0.5">No columns selected</p>
                <p className="text-[10px] text-on-surface-variant">Check fields from the left catalog to add them to your report.</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto max-h-[380px] space-y-2 pr-1">
                {selectedFields.map((field, index) => (
                  <div key={field.key} className="p-2.5 bg-surface rounded-[8px] border border-surface-container-high space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="text-xs font-bold text-primary shrink-0 w-6">#{index + 1}</span>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateFieldProp(field.key, 'label', e.target.value)}
                          placeholder="Header label"
                          className={`${INPUT_SM_CLASS} font-semibold`}
                        />
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => moveField(index, -1)}
                          className="p-1 text-slate-500 hover:text-primary disabled:opacity-30 rounded hover:bg-surface-container"
                          title="Move Up"
                        >
                          <span className="material-symbols-outlined text-sm">arrow_upward</span>
                        </button>
                        <button
                          type="button"
                          disabled={index === selectedFields.length - 1}
                          onClick={() => moveField(index, 1)}
                          className="p-1 text-slate-500 hover:text-primary disabled:opacity-30 rounded hover:bg-surface-container"
                          title="Move Down"
                        >
                          <span className="material-symbols-outlined text-sm">arrow_downward</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => updateFieldProp(field.key, 'visible', !field.visible)}
                          className={`p-1 rounded hover:bg-surface-container ${field.visible ? 'text-primary' : 'text-slate-400'}`}
                          title={field.visible ? 'Visible in report' : 'Hidden from report table'}
                        >
                          <span className="material-symbols-outlined text-sm">{field.visible ? 'visibility' : 'visibility_off'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedFields((prev) => prev.filter((f) => f.key !== field.key))}
                          className="p-1 text-slate-400 hover:text-error rounded hover:bg-surface-container"
                          title="Remove column"
                        >
                          <span className="material-symbols-outlined text-sm">close</span>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-0.5">
                      <div>
                        <label className={LABEL_CLASS}>Data Format</label>
                        <AppDropdown
                          value={field.format || ''}
                          onChange={(val) => updateFieldProp(field.key, 'format', val || null)}
                          options={FORMAT_OPTIONS}
                          triggerClassName={DROPDOWN_TRIGGER_SM_CLASS}
                        />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Width (px)</label>
                        <input
                          type="number"
                          value={field.width || ''}
                          onChange={(e) => updateFieldProp(field.key, 'width', e.target.value ? Number(e.target.value) : null)}
                          placeholder="Auto width"
                          className={INPUT_SM_CLASS}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
